import { useEffect } from "react";
import { useDashboardV2Store } from "@/store/api/dashboardV2Store";
import { useDashboardConfigStore } from "@/store/dashboardConfigStore";
import { DashboardPeriodBar } from "@/components/dashboard/DashboardPeriodBar";
import { useDashboardPeriod } from "@/hooks/useDashboardPeriod";
import { WidgetRenderer } from "@/components/dashboard/WidgetRenderer";
import { useTranslation } from "react-i18next";
import { LayoutGrid } from "lucide-react";
import { useUserStore } from "@/store/userStore";
import { formatDashboardRangeLabel } from "@/utils/dashboardDateUtils";
import i18n from "@/i18n";

const committedRangeLabel = (scope: string | null, period: Parameters<typeof formatDashboardRangeLabel>[1], fallback: string) => {
  if (!scope) return fallback;
  const parts = scope.split(":");
  const startDate = parts.at(-2);
  const endDate = parts.at(-1);
  return startDate && endDate ? formatDashboardRangeLabel({ startDate, endDate }, period, i18n.language) : fallback;
};

export function OverviewTab() {
  const { t } = useTranslation(["dashboard", "adminui2"]);
  const { period, offset, range, label, setPeriod, setOffset } = useDashboardPeriod("overview");
  const gymId = useUserStore((state) => state.gymId);
  const userId = useUserStore((state) => state.user?.id);
  
  const { getVisibleWidgets } = useDashboardConfigStore();
  
  const {
    kpisLoading,
    growthData,
    growthLoading,
    memberEvolutionData,
    memberEvolutionLoading,
    heatmapData,
    heatmapLoading,
    fetchAllKPIs,
    fetchGrowthData,
    fetchMemberEvolution,
    fetchHeatmap,
    fetchInsights,
    beginTenantScope,
    growthError,
    memberEvolutionError,
    heatmapError,
    resourceMeta,
  } = useDashboardV2Store();

  useEffect(() => {
    if (gymId && userId) beginTenantScope(userId, gymId);
  }, [beginTenantScope, gymId, userId]);

  // Phase 1: Load critical KPIs immediately
  useEffect(() => {
    if (gymId && userId) fetchAllKPIs(period, range.effective, { gymId, userId });
  }, [period, range.effective.startDate, range.effective.endDate, fetchAllKPIs, gymId, userId]);

  // Phase 2: Load charts after a short delay (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!gymId || !userId) return;
      fetchGrowthData(period, range.effective, range.comparison, { gymId, userId });
      fetchMemberEvolution(period, range.effective, { gymId, userId });
      fetchHeatmap(period, range.effective, { gymId, userId });
    }, 300);

    return () => clearTimeout(timer);
  }, [period, range.effective.startDate, range.effective.endDate, range.comparison.startDate, range.comparison.endDate, fetchGrowthData, fetchMemberEvolution, fetchHeatmap, gymId, userId]);

  // Phase 3: Load insights after charts (600ms total)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (gymId && userId) fetchInsights(period, range.effective, { gymId, userId });
    }, 600);

    return () => clearTimeout(timer);
  }, [period, range.effective.startDate, range.effective.endDate, fetchInsights, gymId, userId]);

  const visibleWidgets = getVisibleWidgets();

  // Empty state when no widgets are visible
  if (visibleWidgets.length === 0) {
    return (
      <div className="space-y-6">
        <DashboardPeriodBar period={period} offset={offset} label={label} onPeriodChange={setPeriod} onOffsetChange={setOffset} />
        
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
            <LayoutGrid className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">{t("overviewTab.emptyTitle", { ns: "adminui2" })}</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            {t("overviewTab.emptyDesc", { ns: "adminui2" })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <DashboardPeriodBar
        period={period}
        offset={offset}
        label={label}
        isRefreshing={kpisLoading || growthLoading || memberEvolutionLoading || heatmapLoading}
        onPeriodChange={setPeriod}
        onOffsetChange={setOffset}
      />

      {/* Dynamic Widget Rendering */}
      <WidgetRenderer
        period={period}
        kpisLoading={kpisLoading && resourceMeta.kpis.committedScope === null}
        growthData={growthData}
        growthLoading={growthLoading}
        memberEvolutionData={memberEvolutionData}
        memberEvolutionLoading={memberEvolutionLoading}
        heatmapData={heatmapData}
        heatmapLoading={heatmapLoading && resourceMeta.heatmap.committedScope === null}
        range={range.effective}
        growthError={growthError}
        memberEvolutionError={memberEvolutionError}
        growthRefreshing={growthLoading && resourceMeta.growth.committedScope !== null}
        memberEvolutionRefreshing={memberEvolutionLoading && resourceMeta.memberEvolution.committedScope !== null}
        growthContextLabel={committedRangeLabel(resourceMeta.growth.committedScope, period, label)}
        memberEvolutionContextLabel={committedRangeLabel(resourceMeta.memberEvolution.committedScope, period, label)}
        heatmapError={heatmapError}
        heatmapRefreshing={heatmapLoading && resourceMeta.heatmap.committedScope !== null}
        heatmapContextLabel={committedRangeLabel(resourceMeta.heatmap.committedScope, period, label)}
        onRetryHeatmap={() => gymId && userId ? fetchHeatmap(period, range.effective, { gymId, userId }) : undefined}
        onRetryKPIs={() => gymId && userId ? fetchAllKPIs(period, range.effective, { gymId, userId }) : undefined}
        onRetryGrowth={() => gymId && userId ? fetchGrowthData(period, range.effective, range.comparison, { gymId, userId }) : undefined}
        onRetryMemberEvolution={() => gymId && userId ? fetchMemberEvolution(period, range.effective, { gymId, userId }) : undefined}
      />
    </div>
  );
}
